import {
  alpha,
  createTheme,
  type Components,
  type Theme,
  type TypographyVariantsOptions,
} from '@mui/material/styles'

import { typography } from '@hapana/shared/web/styles/material-ui/typography'
import { components } from '@hapana/shared/web/styles/material-ui/components/index'

import { palette, settings_palette } from './palette'
import { dark_palette, dark_settings_palette } from './palette.dark'
import { components as coreComponents } from './components'
import { breakpoints } from './breakpoints'

export const COLOR_MODES = {
  light: 'light',
  dark: 'dark',
} as const

export type ColorMode = (typeof COLOR_MODES)[keyof typeof COLOR_MODES]

export const PALETTE_SCOPES = {
  brand: 'brand',
  location: 'location',
} as const

export type PaletteScope = (typeof PALETTE_SCOPES)[keyof typeof PALETTE_SCOPES]

const PALETTES = {
  light: { brand: palette, location: settings_palette },
  dark: { brand: dark_palette, location: dark_settings_palette },
}

type DarkPalette = (typeof PALETTES.dark)[PaletteScope]

export const selectPalette = (mode: ColorMode, scope: PaletteScope) => PALETTES[mode][scope]

const ALERT_DEFAULT_SEVERITY = 'success'

const baseTypography = typography as TypographyVariantsOptions

const baseComponents = { ...components, ...coreComponents } as Components<Theme>

const buildDarkComponentOverrides = (darkPalette: DarkPalette): Components<Theme> => ({
  MuiCssBaseline: {
    defaultProps: { enableColorScheme: true },
    styleOverrides: {
      '*::-webkit-scrollbar-thumb': {
        boxShadow: `inset 0 0 4px 4px ${darkPalette.border.subtle}`,
      },
      '*::-webkit-scrollbar-thumb:hover': {
        boxShadow: `inset 0 0 4px 4px ${darkPalette.border.default}`,
      },
      '.fc': {
        '--fc-border-color': darkPalette.border.subtle,
        '--fc-page-bg-color': darkPalette.surface.default,
        '--fc-neutral-bg-color': darkPalette.surface.strong,
        '--fc-neutral-text-color': darkPalette.text.secondary,
        '--fc-list-event-hover-bg-color': darkPalette.surface.hover,
        '--fc-today-bg-color': alpha(darkPalette.primary.main, 0.08),
        '--fc-highlight-color': alpha(darkPalette.primary.main, 0.16),
        '--fc-non-business-color': alpha(darkPalette.surface.overlay, 0.25),
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: { backgroundImage: 'none' },
    },
  },
  MuiAlert: {
    styleOverrides: {
      filled: ({ ownerState }) => {
        const tone = darkPalette[ownerState.severity ?? ALERT_DEFAULT_SEVERITY]
        return {
          backgroundColor: tone.lightest,
          color: tone.dark,
          '& .MuiAlert-icon, & .MuiAlert-icon svg, & .MuiAlert-action': { color: tone.dark },
        }
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        background: darkPalette.surface.inverse,
        color: darkPalette.text.inverse,
      },
    },
  },
})

export const createAppTheme = ({ mode, scope }: { mode: ColorMode; scope: PaletteScope }) => {
  const activePalette = selectPalette(mode, scope)
  const modeOverrides =
    mode === COLOR_MODES.dark
      ? { components: buildDarkComponentOverrides(PALETTES.dark[scope]) }
      : {}

  return createTheme(
    {
      typography: {
        ...baseTypography,
        grayHeader4: { ...baseTypography.grayHeader4, color: activePalette.gray[64] },
        graySmall: { ...baseTypography.graySmall, color: activePalette.gray[64] },
      },
      palette: activePalette,
      components: baseComponents,
      breakpoints,
    },
    modeOverrides
  )
}
